import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useI18n } from '../i18n';

const COMPANY_URL = process.env.REACT_APP_COMPANY_URL || 'https://www.pheigroup.com';
const COMPANY_EMAIL = process.env.REACT_APP_COMPANY_EMAIL || 'ceo@pheigroup.com';

export function Nav() {
  const { lang, setLang, t } = useI18n();
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-obsidian/80 border-b hairline">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="serif text-2xl tracking-tight text-white group-hover:text-gold transition-colors" style={{transitionProperty: 'color'}}>Pythia</span>
          <span className="mono text-[10px] uppercase tracking-widest2 text-white/40 hidden lg:inline">A PHEI Group Company</span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 lg:gap-8 mono text-xs uppercase tracking-widest2">
          <NavLink to="/methodology" data-testid="nav-methodology" className={({isActive}) => isActive ? 'text-gold' : 'text-white/70 hover:text-white'}>{t('nav.methodology')}</NavLink>
          <a href="/#products" data-testid="nav-products" className="text-white/70 hover:text-white">{t('nav.products')}</a>
          <Link to="/sample" data-testid="nav-samples" className="text-white/70 hover:text-white">{t('nav.samples')}</Link>
          <Link to="/contact" data-testid="nav-contact" className="text-white/70 hover:text-white">{t('nav.contact')}</Link>
          <Link to="/intake" data-testid="nav-intake" className="text-gold hover:text-white">{t('nav.intake')}</Link>
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === 'en' ? 'es' : 'en')}
            className="mono text-[10px] uppercase tracking-widest2 border hairline px-3 py-1.5 text-white/70 hover:text-gold hover:border-gold/40"
            style={{transitionProperty: 'color, border-color'}}
          >
            {lang === 'en' ? 'EN · ES' : 'ES · EN'}
          </button>
          <Link
            to="/admin"
            data-testid="nav-admin"
            className="hidden md:inline-block mono text-[10px] uppercase tracking-widest2 text-white/50 hover:text-white"
          >
            {t('nav.admin')}
          </Link>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t hairline mt-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-12 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-4">
          <div className="serif text-3xl text-white">Pythia</div>
          <div className="mono text-[10px] uppercase tracking-widest2 text-white/40 mt-2 max-w-xs">{t('footer.tagline')}</div>
          <a href={COMPANY_URL} target="_blank" rel="noreferrer" data-testid="footer-pheigroup" className="mt-4 inline-block mono text-[10px] uppercase tracking-widest2 text-gold hover:text-white">
            A PHEI Group Company → {COMPANY_URL.replace(/https?:\/\//, '')}
          </a>
        </div>
        <div className="md:col-span-2">
          <div className="mono text-[10px] uppercase tracking-widest2 text-white/40 mb-4">Products</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/intake" className="hover:text-gold">Deal Report</Link></li>
            <li><Link to="/intake" className="hover:text-gold">Territory Radar</Link></li>
            <li><Link to="/intake" className="hover:text-gold">Intelligence Desk</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="mono text-[10px] uppercase tracking-widest2 text-white/40 mb-4">Company</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/methodology" className="hover:text-gold">Methodology</Link></li>
            <li><Link to="/sample" className="hover:text-gold">Sample Report</Link></li>
            <li><Link to="/contact" className="hover:text-gold">Custom Analysis</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="mono text-[10px] uppercase tracking-widest2 text-white/40 mb-4">Legal</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/terms" data-testid="footer-terms" className="hover:text-gold">Terms</Link></li>
            <li><Link to="/privacy" data-testid="footer-privacy" className="hover:text-gold">Privacy</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="mono text-[10px] uppercase tracking-widest2 text-white/40 mb-4">Contact</div>
          <a href={`mailto:${COMPANY_EMAIL}`} data-testid="footer-email" className="text-sm text-white/70 hover:text-gold break-all">{COMPANY_EMAIL}</a>
        </div>
      </div>
      <div className="border-t hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row justify-between gap-2 mono text-[10px] uppercase tracking-widest2 text-white/40">
          <div>© {new Date().getFullYear()} PHEI Group · Pythia · {t('footer.rights')}</div>
          <div>Not an appraisal · Not investment advice · Model-assisted due diligence</div>
        </div>
      </div>
    </footer>
  );
}

export function DisclaimerBar() {
  const { t } = useI18n();
  return (
    <div className="border-y hairline bg-surface1/40">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-2.5 flex items-center gap-3 mono text-[10px] uppercase tracking-widest2 text-white/50">
        <span className="h-1.5 w-1.5 bg-gold rounded-full blink" />
        {t('hero.disclaimer')}
      </div>
    </div>
  );
}
